import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, User, Briefcase, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { setSignupDraft } from "@/lib/storage";
import { AuthScreen, AuthBody, ScreenHeader } from "@/screens/auth/_ui";

const TypeCard = ({ icon: Icon, title, onClick, testId, active }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className={[
            "w-full flex items-center gap-4 p-5 rounded-2xl border bg-card hover:bg-accent/50 active:scale-[0.99] transition-[transform,background-color] duration-200 text-left shadow-soft",
            active ? "border-primary bg-primary/5" : "border-border",
        ].join(" ")}
    >
        <span className="h-12 w-12 flex items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon size={22} strokeWidth={2.2} />
        </span>
        <span className="flex-1 font-display text-[15px] font-semibold">{title}</span>
        <ArrowRight size={16} className="text-muted-foreground" />
    </button>
);

const MethodCard = ({ icon: Icon, title, subtitle, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="w-full flex items-center gap-4 p-5 rounded-2xl border border-border bg-card hover:bg-accent/50 active:scale-[0.99] transition-[transform,background-color] duration-200 text-left shadow-soft"
    >
        <span className="h-12 w-12 flex items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon size={22} strokeWidth={2.2} />
        </span>
        <span className="flex-1">
            <span className="block font-display text-[15px] font-semibold">{title}</span>
            <span className="block text-[13px] text-muted-foreground mt-0.5">{subtitle}</span>
        </span>
    </button>
);

const SignupMethod = () => {
    const navigate = useNavigate();
    // Step 1: account type. Step 2: phone/email method.
    const [accountType, setAccountType] = useState(null);

    const choose = (method) => {
        // Start a fresh draft — never prefill anything
        setSignupDraft({ method, accountType });
        navigate("/auth/signup/identifier");
    };

    // "Change" button toggles directly between Personal <-> Business —
    // no detour back to the type-selection step.
    const toggleType = () =>
        setAccountType((t) => (t === "business" ? "personal" : "business"));

    return (
        <AuthScreen testId="signup-method-screen">
            <ScreenHeader title="Create account" />
            <AuthBody>
                <div className="flex flex-col items-center text-center pt-6 pb-6 animate-fade-up">
                    <Logo size={56} />
                    <h2 className="font-display text-[22px] font-semibold tracking-tight mt-4">
                        {accountType ? "How would you like to sign up?" : "Choose account type"}
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground mt-1.5 max-w-xs">
                        {accountType
                            ? "Phone numbers get an SMS code. Emails get a confirmation link."
                            : "Pick the option that fits you best — you can change it later."}
                    </p>
                </div>

                {!accountType ? (
                    <div className="space-y-3 animate-fade-up" data-testid="signup-account-type-step">
                        <TypeCard
                            testId="signup-account-type-personal"
                            icon={User}
                            title="Personal"
                            onClick={() => setAccountType("personal")}
                        />
                        <TypeCard
                            testId="signup-account-type-business"
                            icon={Briefcase}
                            title="Business"
                            onClick={() => setAccountType("business")}
                        />
                    </div>
                ) : (
                    <div className="space-y-3 animate-fade-up" style={{ animationDelay: "60ms" }}>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[11.5px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
                                Signing up as · {accountType === "business" ? "Business" : "Personal"}
                            </span>
                            <button
                                type="button"
                                data-testid="signup-account-type-change"
                                onClick={toggleType}
                                className="text-[12px] font-medium text-primary hover:underline"
                            >
                                Change
                            </button>
                        </div>
                        <MethodCard
                            testId="signup-method-phone"
                            icon={Phone}
                            title="Use phone number"
                            subtitle="Get a 6-digit code via SMS"
                            onClick={() => choose("phone")}
                        />
                        <MethodCard
                            testId="signup-method-email"
                            icon={Mail}
                            title="Use email address"
                            subtitle="Get a confirmation link via email"
                            onClick={() => choose("email")}
                        />
                    </div>
                )}
            </AuthBody>
        </AuthScreen>
    );
};

export default SignupMethod;
